import { useState } from "react";
import { LuFlag, LuPlus, LuScanLine } from "react-icons/lu";
import { Heading, Muted, Small } from "@/components/typography";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Else, ElseIf, If } from "@/components/ui/conditional";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Empty, EmptyIcon, EmptyTitle, EmptyDescription } from "@/components/ui/empty";
import { Fab } from "@/components/ui/fab";
import { Item, ItemContent, ItemTitle, ItemDescription, ItemActions, ItemGroup, ItemHeader, ItemMedia } from "@/components/ui/item";
import { scriptStatusLabel, type ScriptSummary } from "@/lib/script-summary";
import { settings } from "@/lib/settings";
import { useQueueBannerText } from "@/lib/queue-banner";
import { queueHealth, stuckWarning } from "@/lib/queue-health";
import { useAckFlag } from "../hooks/use-ack-flag";
import { useFlagState, useFlaggedIds } from "../lib/flag-message-store";
import { openFlaggedScript } from "../lib/open-flagged-script";
import { createScript, emptyScriptInput, useScriptSummaries } from "../hooks/use-scripts";
import { ForceUploadPanel } from "./force-upload";
import { ResolveFlagDialog } from "./resolve-flag-dialog";
import { UploadNowButton } from "./upload-now";

export function HomePage({
  onOpenScript,
}: {
  onOpenScript: (scriptId: string) => void;
}) {
  const { summaries, isLoading: loading } = useScriptSummaries();
  const scannerName = settings.getScannerName();

  function onNewScript() {
    onOpenScript(createScript(emptyScriptInput()).id);
  }

  const flaggedIds = new Set(useFlaggedIds());
  const flagged = summaries.filter(s => flaggedIds.has(s.script.id));
  const needsAttention = summaries.filter(
    s => !flaggedIds.has(s.script.id) && (!s.counts.fullyUploaded || s.counts.needsAttention),
  );
  const uploaded = summaries.filter(s => !flaggedIds.has(s.script.id) && s.counts.fullyUploaded);
  const pageCount = summaries.reduce((sum, s) => sum + s.counts.total, 0);

  const pages = summaries.flatMap(s => s.pages);
  const health = queueHealth(
    summaries.map(s => s.script),
    pages,
  );
  /** Passing "no pending work" drops only the banner's cheerful
   * "Uploading in the background…" — its offline and paused messages still
   * win, since those come first. That one line sitting above "uploads keep
   * failing after 8 attempts" is the mixed message that let a stalled phone
   * look busy for hours; the panel below is the honest version. */
  const banner = useQueueBannerText(needsAttention.length > 0 && stuckWarning(health) === null);

  return (
    <div className="p-6 pb-20">
      <div className="mb-4">
        <Heading as="h1" variant="h4">
          Exam Scripts
        </Heading>
        <Muted>{scannerName ?? "Unnamed scanner"}</Muted>
      </div>
      <div className="mb-4">
        <If condition={!loading}>
          <Muted>
            {summaries.length} {summaries.length === 1 ? "script" : "scripts"} · {pageCount}{" "}
            {pageCount === 1 ? "page" : "pages"}
          </Muted>
        </If>
      </div>

      <If when={banner}>
        {text => (
          <div className="mb-4 rounded-md bg-muted px-3 py-2">
            <Small>{text}</Small>
          </div>
        )}
      </If>

      <If condition={!loading}>
        <ForceUploadPanel health={health} pages={pages} />
      </If>

      <If condition={loading}>
        <div className="space-y-3">
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
        <ElseIf condition={summaries.length === 0}>
          <Empty>
            <EmptyIcon>
              <LuScanLine />
            </EmptyIcon>
            <EmptyTitle>No scripts yet</EmptyTitle>
            <EmptyDescription>Photograph an exam script to start a new batch.</EmptyDescription>
          </Empty>
        </ElseIf>
        <Else>
          <div className="space-y-6">
            <If condition={flagged.length > 0}>
              <Section title="Flagged" items={flagged} onOpen={onOpenScript} />
            </If>
            <If condition={flagged.length > 0 && (needsAttention.length > 0 || uploaded.length > 0)}>
              <Separator />
            </If>
            <If condition={needsAttention.length > 0}>
              <Section title="Needs attention" items={needsAttention} onOpen={onOpenScript} />
            </If>
            <If condition={needsAttention.length > 0 && uploaded.length > 0}>
              <Separator />
            </If>
            <If condition={uploaded.length > 0}>
              <Section title={`Uploaded (${uploaded.length})`} items={uploaded} onOpen={onOpenScript} />
            </If>
          </div>
        </Else>
      </If>

      <Fab onClick={onNewScript} aria-label="New script">
        <LuPlus />
      </Fab>
    </div>
  );
}

function Section({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: ScriptSummary[];
  onOpen: (scriptId: string) => void;
}) {
  return (
    <div>
      <Muted as="p" className="mb-2 font-medium">
        {title}
      </Muted>
      <ItemGroup>
        {items.map(s => (
          <ScriptItem key={s.script.id} summary={s} onOpen={onOpen} />
        ))}
      </ItemGroup>
    </div>
  );
}

function ScriptItem({
  summary: s,
  onOpen,
}: {
  summary: ScriptSummary;
  onOpen: (scriptId: string) => void;
}) {
  const flag = useFlagState(s.script.id);
  const { ack, pending } = useAckFlag();
  const [resolveOpen, setResolveOpen] = useState(false);
  const unacked = Boolean(flag && !flag.ackedAt);
  const remoteId = s.script.remoteId;

  function handleOpen() {
    openFlaggedScript({
      hasUnackedFlag: unacked && !resolveOpen,
      localId: s.script.id,
      remoteId,
      ack: (localId, id) => {
        if (resolveOpen) return;
        void ack(localId, id).catch(() => {});
      },
      onOpen,
    });
  }

  async function handleResolve(reply: string) {
    if (!remoteId) return;
    try {
      await ack(s.script.id, remoteId, { reply, optimistic: false });
      setResolveOpen(false);
    } catch {
      // toast already shown in useAckFlag
    }
  }

  return (
    <>
      <Item render={<button type="button" onClick={handleOpen} />}>
        <ItemHeader>
          <ItemMedia variant="icon">
            <LuScanLine />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>{s.displayLabel}</ItemTitle>
            <ItemDescription>
              {s.counts.uploaded}/{s.counts.total} uploaded
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <StatusLabel summary={s} />
          </ItemActions>
        </ItemHeader>
        <If condition={s.counts.total > 0}>
          <Progress value={s.counts.uploaded} max={s.counts.total} />
        </If>
      </Item>
      <UploadNowButton pages={s.pages} />
      <If when={flag?.message ? flag : null}>
        {f => (
          <Alert variant="warning">
            <LuFlag />
            <AlertTitle>{unacked ? "Flagged" : "Marked resolved"}</AlertTitle>
            <AlertDescription>
              {f.message}
              <If when={f.reply}>
                {reply => (
                  <>
                    <br />
                    Note: {reply}
                  </>
                )}
              </If>
            </AlertDescription>
            <AlertAction>
              <div className="flex flex-col gap-1.5 sm:flex-row">
                <Button size="sm" variant="warning" disabled={pending} onClick={handleOpen}>
                  {unacked ? "Open script" : "Reopen"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending || !remoteId}
                  onClick={() => setResolveOpen(true)}
                >
                  {unacked ? "Mark resolved" : "Update note"}
                </Button>
              </div>
            </AlertAction>
          </Alert>
        )}
      </If>
      <ResolveFlagDialog
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        onSubmit={reply => void handleResolve(reply)}
        submitting={pending}
        initialReply={flag?.reply ?? ""}
      />
    </>
  );
}

function StatusLabel({ summary }: { summary: ScriptSummary }) {
  const label = scriptStatusLabel(summary.script.uploadEnabled, summary.counts);
  const attention = summary.counts.blocked > 0 || summary.counts.failed > 0;
  const quiet = label === "Done" || label === "Not done";
  return (
    <Badge variant={attention ? "destructive" : quiet ? "secondary" : "default"}>{label}</Badge>
  );
}
